/**
 * The message/streaming state machine.
 *
 * Owns one turn from start to finish: append the question, decide whether a provider
 * should even be called, consume the event stream, and settle the assistant message into
 * exactly one terminal state — complete, cancelled, or error. No component branches on
 * partial state; they read `status` and paint it.
 *
 * The provider is reached through the `AskProvider` interface only (provider.ts). Nothing
 * here knows which model, or that there currently is not one.
 */
import { useCallback, useEffect } from 'react'

import { useFeatureSwitches } from 'hooks/api/useFeatures'

import { retrieveDocContext } from './helpContext'
import { askProvider } from './provider'
import type { AskCitation, AskMessage, AskToolCall } from './types'
import { useAskAccess } from './useAskAccess'
import { useAskStore } from './useAskStore'

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

/**
 * In-flight turn state, held at MODULE scope rather than in the component.
 *
 * A turn is application state, not component state. This hook is consumed by AskComposer,
 * which only mounts inside the conversation view — so while these lived in refs with an
 * abort-on-unmount cleanup, flipping the panel to History unmounted the composer and
 * silently cancelled the answer being streamed. The turn settled as `cancelled` with no
 * user action that reads as "stop". Glancing at your history should not destroy the thing
 * you are waiting for.
 *
 * Everything a turn writes goes through `useAskStore.getState()`, which is itself
 * module-scoped, so a turn runs to completion with no component mounted at all: switch to
 * History mid-stream and back, and the answer is there, finished or still streaming.
 *
 * There is exactly one Ask panel in the app, so a single module-level slot is the right
 * shape. `stop()` is now the only thing that cancels a turn — which is what the button
 * already implied.
 */
let activeAbort: AbortController | null = null
let activeRun = false

export const useAskRunner = () => {
  const activeId = useAskStore(s => s.activeId)
  const conversations = useAskStore(s => s.conversations)
  const startConversation = useAskStore(s => s.startConversation)
  const appendMessage = useAskStore(s => s.appendMessage)
  const updateMessage = useAskStore(s => s.updateMessage)
  const consumePendingQuestion = useAskStore(s => s.consumePendingQuestion)
  const open = useAskStore(s => s.open)
  // VRG-677: decided before anything is sent. The composer disables itself on this too;
  // this guard is for the paths that send WITHOUT the composer (a question carried over
  // from ⌘K search), so they get the same explanation instead of a round trip to a 403.
  const access = useAskAccess()
  const askDeniedMessage = access.message
  // VRG-680 — gated docs (Refer & earn while its switch is off) are never retrieved.
  const { switches } = useFeatureSwitches()

  const active = conversations.find(c => c.id === activeId)
  const last = active?.messages[active.messages.length - 1]
  const busy = last?.role === 'assistant' && last.status === 'streaming'

  const send = useCallback(
    async (rawQuestion: string) => {
      const question = rawQuestion.trim()
      // Guard re-entry: two turns writing into the same conversation would interleave
      // deltas into each other's message.
      if (!question || activeRun) return
      activeRun = true

      const conversationId = useAskStore.getState().activeId ?? startConversation()

      appendMessage(conversationId, {
        id: newId(),
        role: 'user',
        text: question,
        createdAt: Date.now(),
      })

      if (askDeniedMessage) {
        appendMessage(conversationId, {
          id: newId(),
          role: 'assistant',
          text: '',
          createdAt: Date.now(),
          status: 'error',
          error: askDeniedMessage,
        })
        activeRun = false
        return
      }

      // Retrieval runs for EVERY turn, unconditionally — there is no user-picked mode to
      // gate it on, and the question itself does not announce what it is about. An empty
      // result must not short-circuit the turn the way it used to: "where are my
      // certificates scanned most" legitimately matches no docs and still has to reach
      // the provider, where a tool can answer it. Only the provider — having tried both
      // grounding and tools — gets to decide a turn is genuinely uncovered.
      //
      // Awaited: a heading-level match now has to load the guide body it points at (VRG-227)
      // to slice out its own section, so this is no longer synchronous. The provider must
      // never see the old thin, doc-lead-only context — awaiting here, before the provider
      // call below, is what guarantees that.
      const context = await retrieveDocContext(question, undefined, switches)

      const assistantId = newId()
      appendMessage(conversationId, {
        id: assistantId,
        role: 'assistant',
        text: '',
        createdAt: Date.now(),
        status: 'streaming',
        // Seeded from whatever retrieval found, if anything, so a provider that never
        // emits its own citations event still produces a sourced answer when docs matched.
        citations:
          context.length > 0
            ? context.map<AskCitation>(c => ({
                kind: 'doc',
                slug: c.slug,
                category: c.category,
                title: c.title,
                headingId: c.headingId,
                headingText: c.headingText,
              }))
            : undefined,
      })

      const controller = new AbortController()
      activeAbort = controller

      let text = ''
      const toolCalls: AskToolCall[] = []

      try {
        const history = (useAskStore.getState().conversations.find(c => c.id === conversationId)
          ?.messages ?? []) as AskMessage[]

        for await (const event of askProvider.stream({
          question,
          history: history.filter(m => m.id !== assistantId),
          context,
          signal: controller.signal,
        })) {
          if (controller.signal.aborted) break

          switch (event.type) {
            case 'text':
              text += event.delta
              updateMessage(conversationId, assistantId, { text })
              break
            case 'citations':
              updateMessage(conversationId, assistantId, { citations: event.citations })
              break
            case 'tool_call':
              toolCalls.push(event.call)
              updateMessage(conversationId, assistantId, { toolCalls: [...toolCalls] })
              break
            case 'tool_result': {
              const call = toolCalls.find(c => c.name === event.name && c.status === 'running')
              if (call) {
                call.status = event.error ? 'error' : 'complete'
                call.summary = event.summary
                call.error = event.error
                call.result = event.result
                updateMessage(conversationId, assistantId, { toolCalls: [...toolCalls] })
              }
              break
            }
            case 'no_docs':
              updateMessage(conversationId, assistantId, { uncovered: { kind: 'no_docs' } })
              break
            case 'no_data':
              updateMessage(conversationId, assistantId, {
                uncovered: { kind: 'no_data', missing: event.missing },
              })
              break
            case 'no_market_data':
              updateMessage(conversationId, assistantId, {
                uncovered: { kind: 'no_market_data' },
              })
              break
            case 'error':
              updateMessage(conversationId, assistantId, {
                status: 'error',
                error: event.message,
              })
              activeRun = false
              activeAbort = null
              return
            case 'done':
              break
          }
        }

        updateMessage(conversationId, assistantId, {
          status: controller.signal.aborted ? 'cancelled' : 'complete',
        })
      } catch (err) {
        // An abort surfaces as a throw from some transports and as a clean break from
        // others, so both paths have to land on 'cancelled' rather than 'error' —
        // otherwise pressing stop looks like a failure.
        const aborted = controller.signal.aborted || (err as Error)?.name === 'AbortError'
        updateMessage(conversationId, assistantId, {
          status: aborted ? 'cancelled' : 'error',
          error: aborted ? undefined : (err as Error)?.message ?? 'Something went wrong.',
        })
      } finally {
        activeRun = false
        activeAbort = null
      }
    },
    [appendMessage, updateMessage, startConversation, askDeniedMessage, switches],
  )

  const stop = useCallback(() => {
    activeAbort?.abort()
  }, [])

  /**
   * Pick up a question handed over from the ⌘K palette.
   *
   * Consumed (not merely read) so it fires once — the store clears it as it hands it
   * over, which is what stops a re-render from asking the same question twice.
   */
  useEffect(() => {
    if (!open) return
    const pending = consumePendingQuestion()
    if (pending) void send(pending)
  }, [open, consumePendingQuestion, send])

  // NOTE: there is deliberately no abort-on-unmount cleanup. Unmounting is not a
  // cancellation signal here — the composer unmounts whenever the panel flips to
  // History, and cancelling then threw away an answer the user never asked to stop.

  return { send, stop, busy, configured: askProvider.configured, access }
}
